import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import Icon from '../ui/Icon';
import './Lightbox.css';

export interface LightboxImage {
  src: string;
  alt: string;
  caption?: string;
}

interface LightboxProps {
  image: LightboxImage | null;
  onClose: () => void;
}

/**
 * Modal image viewer. Focus moves to the close button on open, stays inside the dialog,
 * Escape or a backdrop click closes it, and focus returns to the element that opened it.
 */
const Lightbox: React.FC<LightboxProps> = ({ image, onClose }) => {
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const isOpen = image !== null;

  useEffect(() => {
    if (!isOpen) return;
    returnFocusRef.current = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      } else if (e.key === 'Tab') {
        // The close button is the only focusable element: keep focus on it.
        e.preventDefault();
        closeRef.current?.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousOverflow;
      returnFocusRef.current?.focus();
    };
  }, [isOpen, onClose]);

  if (!image) return null;

  return createPortal(
    <div className="lightbox" role="dialog" aria-modal="true" aria-label={image.caption ?? image.alt} onClick={onClose}>
      <figure className="lightbox__content" onClick={(e) => e.stopPropagation()}>
        <button ref={closeRef} type="button" className="lightbox__close" onClick={onClose} aria-label="Close image">
          <Icon icon={X} size={22} />
        </button>
        <img src={image.src} alt={image.alt} className="lightbox__image" decoding="async" />
        {image.caption && <figcaption className="lightbox__caption">{image.caption}</figcaption>}
      </figure>
    </div>,
    document.body
  );
};

export default Lightbox;
